import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Typography, ListItem, ListItemText, ListItemAvatar } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { dateIsOutsideOpeningHours, getNewEndTime } from 'common/utils/dateCalculations';
import { localFormat } from 'common/utils/dateUtils';
import { getDurationString } from 'common/utils/dateCalculations';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';
import StripePattern from 'common/components/StripePattern';
import { Duration, OpeningHoursWithExceptions } from 'common/types';
import {
	StartTimeObject,
	TimeSelectorArgs,
	TimeSelectorStyling,
} from 'components/TimeSelector/types';
import { AvailabilityStatusBubble } from 'components/AvailabilityStatusBubble';
import { AvailabilityStatusType } from 'services/types';

interface TimeSlotProps {
	innerRef?: React.RefObject<HTMLDivElement> | null;
	timeSlotObject: StartTimeObject; // time of the timeslot
	isSelected: boolean; // check if timeslot time and selected time is the same
	onSelect: (date: moment.Moment) => void;
	isInvalid: boolean;
	isClosed: boolean;
	selectorType: TimeSelectorArgs; // checks if start or endDate type
	loading: boolean;
	showAvailability: boolean;
	duration: Duration; // in order to get the duration string & possible endtime
	openingHours: OpeningHoursWithExceptions;
	styling?: TimeSelectorStyling;
}
const TimeSlot = (props: TimeSlotProps) => {
	const {
		timeSlotObject,
		isSelected,
		onSelect,
		isInvalid,
		isClosed,
		selectorType,
		loading,
		showAvailability,
		duration,
		openingHours,
		styling,
	} = props;
	const { time, diffToStart } = timeSlotObject;
	const classes = useStyles();
	const { t, lang } = useTranslation();
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);

	const isEndDate = selectorType.type === 'endTime';
	const from = selectorType.type === 'endTime' ? selectorType.startTime : time;
	const to = isEndDate
		? time
		: moment(getNewEndTime(from.toISOString(), duration, openingHours, shopTimeZone));

	const isEndTimeOutsideHours = dateIsOutsideOpeningHours(to, openingHours);

	const timeOutsideHours = dateIsOutsideOpeningHours(time, openingHours);

	const getPrimaryText = () => {
		if (diffToStart !== null) {
			return t('timeLocationSelector.until', {
				time: localFormat(time, 'HH:mm', shopDateFormat),
				defaultValue: 'Until {{time}}',
			});
		} else {
			return t('timeLocationSelector.from', {
				time: localFormat(time, 'HH:mm', shopDateFormat),
				defaultValue: 'From {{time}}',
			});
		}
	};

	const getSecondaryText = () => {
		if (diffToStart !== null) {
			return getDurationString(
				{
					durationInSeconds: diffToStart,
					durationType: duration.durationType,
					durationName: null,
				},
				'long',
				lang,
				t,
			);
		} else {
			const endMoment = moment(to);
			return t('timeLocationSelector.to', {
				time1: localFormat(endMoment, 'ddd DD.MM. HH:mm', shopDateFormat),
				defaultValue: 'To {{time1}}',
			});
		}
	};

	const getOutsideOfHoursText = () => {
		return t('timeLocationSelector.endsOutsideHours', 'Ends outside opening hours.');
	};

	const disabledItem =
		loading ||
		(!allowOverbookings &&
			isInvalid &&
			selectorType.type === 'endTime' &&
			styling?.disableUnavailableTimeSlots);

	const availabilityStatus: AvailabilityStatusType = loading
		? 'loading'
		: isInvalid
		? 'unavailable'
		: 'available';

	return (
		<ListItem
			key={time.unix()}
			divider
			button
			onClick={() => {
				onSelect(time);
			}}
			disabled={disabledItem}
			className={isSelected ? classes.selectedTimeSlot : classes.timeSlot}
			ref={props.innerRef}
		>
			{showAvailability && (
				<ListItemAvatar classes={{ root: classes.statusAvatar }}>
					<AvailabilityStatusBubble status={availabilityStatus} />
				</ListItemAvatar>
			)}
			<ListItemText
				disableTypography
				primary={<Typography variant="subtitle1">{getPrimaryText()}</Typography>}
				secondary={
					<>
						{((isEndTimeOutsideHours && !isEndDate) || timeOutsideHours) && (
							<StripePattern
								color="dark"
								opacity={0.2}
								degrees={135}
								stripeWidth={2}
								nonStripeColorOpacity={0.0}
							/>
						)}
						<Typography className={classes.timeSlotSecondary} variant="caption" color="secondary">
							{isClosed
								? `${t('timeLocationSelector.storeClosed', 'Store closed')}. ${getSecondaryText()}`
								: isEndTimeOutsideHours && !isEndDate
								? `${getOutsideOfHoursText()}. ${getSecondaryText()}`
								: getSecondaryText()}
						</Typography>
					</>
				}
			/>
		</ListItem>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		statusAvatar: {
			minWidth: 0,
		},
		timeSlotSecondary: {
			color: theme.palette.text.secondary,
		},
		selectedTimeSlot: {
			background: theme.palette.action.selected,
			border: `2px dashed ${theme.palette.common.black}`,
		},
		timeSlot: {
			border: `2px solid transparent`,
		},
	}),
);

export default TimeSlot;
